import { IdentifiableTableColumnInfo } from "@/common/types";
import { TableSettingsCache } from "@/components/atoms/customTable";
import { TableColumnId } from "@fluentui/react-components";
import { useCallback, useMemo, useState } from "react";

export interface ColumnManagementFeatures<TColumn> {
	removeColumn: (index: number) => void;
	addDeletedColumn: () => void;
	resetDefaultColumns: () => void;
	resetVisibleDefaultColumns: () => void;
	columns: TColumn[];
	addColumn: (TableColumnId: TableColumnId) => void;
}

/**
 * @param excludedColumnIds Columns the tenant configuration does not support right now (e.g. Skills
 * when no queue uses skill-based routing, Tags when no tag exists). They are dropped from the initial
 * column set, from "add deleted column" and from a settings reset, and the visible set follows the
 * list while mounted — a column is re-inserted when it leaves the list and dropped when it enters it —
 * so the agent never sees, and cannot bring back, a column with nothing to show. Distinct from a
 * hidden column, which the agent dismissed themselves and can restore. Callers must keep this list out
 * of the persisted hiddenColumnIds, or an exclusion would read back as a deliberate hide once the
 * tenant configuration changes.
 */
export const useColumnManagement = <
	TColumn extends IdentifiableTableColumnInfo,
>(
	columnsDefinition: TColumn[],
	tableSettingsCache: TableSettingsCache,
	excludedColumnIds: string[] = []
): ColumnManagementFeatures<TColumn> => {
	const hiddenColumnIds = useMemo(
		() => tableSettingsCache?.hiddenColumnIds ?? [],
		[tableSettingsCache?.hiddenColumnIds?.join(",")]
	);

	// Joined so the callbacks below depend on the contents, not on the array identity a caller happens
	// to hand us.
	const excludedColumnIdsKey = excludedColumnIds.join(",");

	const getCurrentColumnDefinitions = useCallback(
		() =>
			excludedColumnIds.length
				? columnsDefinition.filter(
						(column) => !excludedColumnIds.includes(column.columnId.toString())
					)
				: columnsDefinition,
		[columnsDefinition, excludedColumnIdsKey]
	);

	const getVisibleColumnDefinitions = useCallback(
		() =>
			getCurrentColumnDefinitions().filter(
				(x) => !hiddenColumnIds.includes(x.columnId.toString())
			),
		[getCurrentColumnDefinitions, hiddenColumnIds]
	);

	// Excluded columns are filtered out of the initial state too, not only out of reset/add-deleted:
	// this state is initialised once, so for a caller that never calls addColumn/removeColumn there is
	// no other place for the exclusion to take effect.
	const [columns, setColumns] = useState<TColumn[]>(
		getVisibleColumnDefinitions
	);

	// Keeps the visible set in sync when the exclusion list changes while mounted: a column that
	// leaves the set (the tenant's first tag is created) is re-inserted at its definition position,
	// one that enters it (the last tag is deleted) is dropped. Applied during render rather than in
	// an effect so callers' persistence effects only ever observe the settled column set and never
	// mistake a mid-sync snapshot for an agent's deliberate hide.
	const [prevExcludedColumnIdsKey, setPrevExcludedColumnIdsKey] = useState(
		excludedColumnIdsKey
	);

	if (prevExcludedColumnIdsKey !== excludedColumnIdsKey) {
		const previouslyExcludedIds = prevExcludedColumnIdsKey
			? prevExcludedColumnIdsKey.split(",")
			: [];

		setPrevExcludedColumnIdsKey(excludedColumnIdsKey);
		setColumns((state) => {
			const remaining = state.filter(
				(column) => !excludedColumnIds.includes(column.columnId.toString())
			);
			const readmitted = columnsDefinition.filter(
				(column) =>
					previouslyExcludedIds.includes(column.columnId.toString()) &&
					!excludedColumnIds.includes(column.columnId.toString()) &&
					!remaining.some(({ columnId }) => columnId === column.columnId)
			);

			if (!readmitted.length) {
				return remaining.length === state.length ? state : remaining;
			}

			const synced = [...remaining];
			for (const column of readmitted) {
				const definitionIndex = columnsDefinition.findIndex(
					({ columnId }) => columnId === column.columnId
				);

				synced.splice(Math.min(definitionIndex, synced.length), 0, column);
			}

			return synced;
		});
	}

	// Restores the right-most hidden column: agents dismiss columns left to right, so the definition
	// list is scanned from the end to bring back the most recently removed one first.
	const addDeletedColumn = useCallback(() => {
		const currentColumnDefinitons = getCurrentColumnDefinitions();
		const currentColumnIds = columns.map(({ columnId }) => columnId);

		let missingColumnIndex = -1;
		for (let i = currentColumnDefinitons.length - 1; i >= 0; i--) {
			const definition = currentColumnDefinitons[i];
			if (definition && !currentColumnIds.includes(definition.columnId)) {
				missingColumnIndex = i;
				break;
			}
		}

		if (missingColumnIndex === -1) {
			return;
		}

		const missingColumn = currentColumnDefinitons[missingColumnIndex];
		if (!missingColumn) {
			return;
		}

		// The insert position is the count of visible columns defined before the restored one, which
		// differs from its definition index whenever an earlier column is still hidden.
		const insertIndex = currentColumnDefinitons
			.slice(0, missingColumnIndex)
			.filter(({ columnId }) => currentColumnIds.includes(columnId)).length;

		setColumns((state) => [
			...state.slice(0, insertIndex),
			missingColumn,
			...state.slice(insertIndex),
		]);
	}, [columns, tableSettingsCache, getCurrentColumnDefinitions]);

	const addColumn = useCallback(
		(tableColumnId: TableColumnId) => {
			const columnToAddIndex = columnsDefinition.findIndex(
				({ columnId }) => tableColumnId === columnId
			);
			const columnToAdd =
				columnToAddIndex === -1
					? undefined
					: columnsDefinition[columnToAddIndex];

			if (!columnToAdd) {
				return;
			}

			// The membership check lives inside the updater so it sees the committed state: callers sync
			// this from an effect that fires whenever a tenant flag flips, and the column is usually
			// already there. Inserting unconditionally produced a second header cell with the same
			// columnId while the row body still rendered one, shifting every later column.
			setColumns((state) =>
				state.some(({ columnId }) => columnId === tableColumnId)
					? state
					: [
							...state.slice(0, columnToAddIndex),
							columnToAdd,
							...state.slice(columnToAddIndex),
						]
			);
		},
		[columnsDefinition]
	);

	const removeColumn = useCallback(
		(index: number) => {
			setColumns([...columns.slice(0, index), ...columns.slice(index + 1)]);
		},
		[columns, tableSettingsCache]
	);

	const resetDefaultColumns = useCallback(() => {
		setColumns(getCurrentColumnDefinitions());
	}, [getCurrentColumnDefinitions]);

	const resetVisibleDefaultColumns = useCallback(() => {
		setColumns(getVisibleColumnDefinitions());
	}, [getVisibleColumnDefinitions]);

	return {
		columns,
		removeColumn,
		addDeletedColumn,
		resetDefaultColumns,
		resetVisibleDefaultColumns,
		addColumn,
	};
};
